import { useParams } from 'react-router-dom';

import { ExperimentNames, FeatureFlagName } from 'src/common/constants';
import { TaskType } from 'src/common/enums/task';
import { useFeature } from 'src/common/growthbook';
import { useFeatureFlag } from 'src/common/hooks';
import { useGetUserQuery } from 'src/common/slices';
import { useGetExperimentGroupsQuery } from 'src/common/slices/experiments';

const EXPERIMENT_START_DATE = new Date('2024-10-23T01:00:00Z');
const EXPERIMENT_END_DATE = new Date('2024-11-14T01:00:00Z');

export enum ExperimentSupportedRoomV1 {
  VULNIVERSITY = 'vulnversity',
  BASIC_PENTESTING = 'basicpentestingjt',
  BLUE = 'blue',
  LINUX_FUNDAMENTALS_PART2 = 'linuxfundamentalspart2',
  LINUX_FUNDAMENTALS_PART3 = 'linuxfundamentalspart3',
  ROOT_ME = 'rrootme',
  PICKLE_RICK = 'picklerick',
  WALKING_AN_APPLICATION = 'walkinganapplication',
  METASPLOIT_INTRO = 'metasploitintro',
  KENOBI = 'kenobi',
}

export enum ExperimentSupportedRoomV2 {
  VULNIVERSITY = 'vulnversity',
  BASIC_PENTESTING = 'basicpentestingjt',
  BLUE = 'blue',
  LINUX_FUNDAMENTALS_PART2 = 'linuxfundamentalspart2',
  LINUX_FUNDAMENTALS_PART3 = 'linuxfundamentalspart3',
  ROOT_ME = 'rrootme',
  PICKLE_RICK = 'picklerick',
  WALKING_AN_APPLICATION = 'walkinganapplication',
  METASPLOIT_INTRO = 'metasploitintro',
  KENOBI = 'kenobi',
  NETWORK_SERVICES = 'networkservices',
}

enum ExperimentVariations {
  Control = 0,
  Treatment = 1,
}

const isSupportedRoom = ({ roomCode, experimentVersion }: { roomCode?: string; experimentVersion: 'v1' | 'v2' }) =>
  Object.values(experimentVersion === 'v1' ? ExperimentSupportedRoomV1 : ExperimentSupportedRoomV2).includes(roomCode);
export const isSupportedTask = (taskType: TaskType) => [TaskType.VM, TaskType.THM].includes(taskType);

export const useCombineStartMachineAttackboxWidgetV2Experiment = () => {
  const { experimentResult } = useFeature('combine-start-machine-attackbox-widget-v2');
  const { roomCode } = useParams();
  const isInExperiment =
    !!experimentResult?.inExperiment &&
    isSupportedRoom({
      roomCode,
      experimentVersion: 'v2',
    });
  return {
    isEnabled: isInExperiment && experimentResult?.variationId === ExperimentVariations.Treatment,
    isControlGroup: isInExperiment && experimentResult?.variationId === ExperimentVariations.Control,
  };
};

export const useCombineStartMachineAttackboxWidgetV1Experiment = () => {
  const { enabled: isFeatureFlagEnabled } = useFeatureFlag(FeatureFlagName.COMBINE_START_MACHINE_ATTACKBOX_WIDGET);

  const { roomCode } = useParams();
  const { data: userData } = useGetUserQuery();
  const userDateSignUp = !!userData?.data?.user?.dateSignUp && new Date(userData.data.user.dateSignUp);
  const hasUserSignedUpAfterExperimentEndDate = userDateSignUp && userDateSignUp > EXPERIMENT_END_DATE;
  const hasUserSignedUpAfterExperimentStartDate = userDateSignUp && userDateSignUp > EXPERIMENT_START_DATE;

  const hasUserSignedUpDuringExperiment =
    hasUserSignedUpAfterExperimentStartDate && !hasUserSignedUpAfterExperimentEndDate;

  const isExperimentEnabled =
    isFeatureFlagEnabled &&
    isSupportedRoom({
      roomCode,
      experimentVersion: 'v1',
    });

  const { data: experimentData } = useGetExperimentGroupsQuery(
    [ExperimentNames.COMBINE_START_MACHINE_ATTACKBOX_WIDGET],
    {
      skip: !isExperimentEnabled || !hasUserSignedUpDuringExperiment,
    },
  );
  const { isControlGroup: isV2ControlGroup } = useCombineStartMachineAttackboxWidgetV2Experiment();

  if (!roomCode || !isExperimentEnabled) {
    return false;
  }

  if (hasUserSignedUpAfterExperimentEndDate || isV2ControlGroup) {
    return true;
  }

  if (hasUserSignedUpDuringExperiment) {
    return experimentData?.data?.[ExperimentNames.COMBINE_START_MACHINE_ATTACKBOX_WIDGET] === 'treatment';
  }

  return false;
};
